import React, { useState, useEffect } from 'react';
import { Award, Star, Trophy, Target, Calendar, Flame } from 'lucide-react';
import { useChildren } from '../hooks/useChildren';
import { supabase, Database } from '../lib/supabase';
import { ChildCard } from '../components/ChildCard';

type Achievement = Database['public']['Tables']['achievements']['Row'];
type ChildAchievement = Database['public']['Tables']['child_achievements']['Row'] & {
  achievements: Achievement;
};

const achievementIcons = {
  Star,
  Trophy,
  Target,
  Award,
  Calendar,
  Flame,
};

export function AchievementsPage() {
  const { children } = useChildren();
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [childAchievements, setChildAchievements] = useState<ChildAchievement[]>([]);
  const [dailyStreak, setDailyStreak] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const selectedChildData = children.find(child => child.id === selectedChild);

  useEffect(() => {
    fetchAchievements();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildAchievements();
      fetchDailyStreak();
    }
  }, [selectedChild]);

  const fetchAchievements = async () => {
    try {
      const { data, error } = await supabase
        .from('achievements')
        .select('*')
        .order('points_required', { ascending: true });

      if (error) throw error;
      setAchievements(data || []);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const fetchChildAchievements = async () => {
    if (!selectedChild) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('child_achievements')
        .select(`
          *,
          achievements (*)
        `)
        .eq('child_id', selectedChild)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      setChildAchievements(data || []);
    } catch (error) {
      console.error('Error fetching child achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyStreak = async () => {
    if (!selectedChild) return;

    try {
      const { data, error } = await supabase
        .from('daily_streaks')
        .select('*')
        .eq('child_id', selectedChild)
        .single();

      if (error) throw error;
      setDailyStreak(data);
    } catch (error) {
      console.error('Error fetching daily streak:', error);
    }
  };

  const isAchievementEarned = (achievementId: string) => {
    return childAchievements.some(ca => ca.achievement_id === achievementId);
  };

  const canEarnAchievement = (achievement: Achievement) => {
    if (!selectedChildData) return false;
    return selectedChildData.total_points >= achievement.points_required;
  };

  if (children.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Award className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Achievements Yet</h2>
        <p className="text-gray-600">Add a child and complete activities to start earning achievements.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Child Selection */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Achievements & Rewards</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((child) => (
            <ChildCard
              key={child.id}
              child={child}
              onClick={() => setSelectedChild(child.id)}
              isSelected={selectedChild === child.id}
            />
          ))}
        </div>
      </div>

      {/* Achievement Details */}
      {selectedChild && selectedChildData && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedChildData.name}'s Achievements
            </h3>
            <p className="text-gray-600">Celebrate learning milestones and accomplishments</p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading achievements...</p>
            </div>
          ) : (
            <>
              {/* Achievement Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{childAchievements.length}</div>
                  <div className="text-sm text-gray-600">Achievements Earned</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Flame className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{dailyStreak?.current_streak || 0}</div>
                  <div className="text-sm text-gray-600">Day Streak</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{dailyStreak?.longest_streak || 0}</div>
                  <div className="text-sm text-gray-600">Best Streak</div>
                </div>
              </div>

              {/* Achievements Grid */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">All Achievements</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {achievements.map((achievement) => {
                    const earned = isAchievementEarned(achievement.id);
                    const canEarn = canEarnAchievement(achievement);
                    const IconComponent = achievementIcons[achievement.icon as keyof typeof achievementIcons] || Award;

                    return (
                      <div
                        key={achievement.id}
                        className={`relative bg-white rounded-xl p-6 shadow-md border-2 transition-all duration-200 ${
                          earned
                            ? 'border-green-300 bg-green-50'
                            : canEarn
                            ? 'border-yellow-300 bg-yellow-50'
                            : 'border-gray-200'
                        }`}
                      >
                        {earned && (
                          <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <Star className="w-4 h-4 text-white" />
                          </div>
                        )}

                        <div className="text-center">
                          <div
                            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                              earned
                                ? 'bg-green-100'
                                : canEarn
                                ? 'bg-yellow-100'
                                : 'bg-gray-100'
                            }`}
                            style={{ backgroundColor: earned ? achievement.badge_color : undefined }}
                          >
                            <IconComponent
                              className={`w-8 h-8 ${
                                earned
                                  ? 'text-white'
                                  : canEarn
                                  ? 'text-yellow-600'
                                  : 'text-gray-400'
                              }`}
                            />
                          </div>

                          <h5 className="font-semibold text-gray-900 mb-2">{achievement.name}</h5>
                          <p className="text-sm text-gray-600 mb-3">{achievement.description}</p>

                          <div className="flex items-center justify-center space-x-2 text-sm">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <span className="text-gray-700">{achievement.points_required} points required</span>
                          </div>

                          {earned && (
                            <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <Trophy className="w-3 h-3 mr-1" />
                              Earned!
                            </div>
                          )}

                          {!earned && canEarn && (
                            <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Target className="w-3 h-3 mr-1" />
                              Ready to earn!
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Achievements */}
              {childAchievements.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h4>
                  <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      {childAchievements.slice(0, 5).map((childAchievement, index) => {
                        const IconComponent = achievementIcons[childAchievement.achievements.icon as keyof typeof achievementIcons] || Award;
                        
                        return (
                          <div key={childAchievement.id} className={`p-4 ${index !== childAchievements.length - 1 ? 'border-b border-gray-100' : ''}`}>
                            <div className="flex items-center space-x-4">
                              <div
                                className="w-12 h-12 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: childAchievement.achievements.badge_color }}
                              >
                                <IconComponent className="w-6 h-6 text-white" />
                              </div>
                              
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900">{childAchievement.achievements.name}</div>
                                <div className="text-sm text-gray-600">{childAchievement.achievements.description}</div>
                              </div>
                              
                              <div className="text-right">
                                <div className="text-sm text-gray-500">
                                  {new Date(childAchievement.earned_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}